/**
 * ProfilTab.jsx — Onglet "Profil" natif : réglages (langue, thème, installation app,
 * tour guidé, déconnexion) regroupés au-dessus de l'abonnement, façon écran "Réglages" d'une
 * app iOS.
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, LogOut, Download, Moon, UserCog, ChevronRight, PlayCircle, Compass, Coins, Loader2, Contrast, Type } from 'lucide-react';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Switch } from '../../components/ui/switch';
import { SubscriptionTab } from './SubscriptionTab';
import { useTour } from '../../context/TourContext';
import { useClientAuth } from '../../context/ClientAuthContext';
import { useClientSettings, TEXT_SCALE_LEVELS } from '../../context/ClientSettingsContext';

const ROW = 'w-full flex items-center justify-between gap-3 px-3.5 py-3.5 rounded-xl hover:bg-white/5 transition-colors text-left';
const TEXT_SCALE_LABELS = { base: { fr: 'Normal', en: 'Normal' }, lg: { fr: 'Grand', en: 'Large' }, xl: { fr: 'Très grand', en: 'Extra large' } };

/** Historique des achats de crédits (bibliothèque d'exercices IA) — filet de transparence
 * pour que le client retrouve facilement ses paiements ponctuels de crédits. */
const CreditPurchaseHistory = ({ en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    authFetch('/api/tracking/custom-exercises/credits/purchase-history')
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
    // eslint-disable-next-line
  }, []);

  if (items !== null && items.length === 0) return null;

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4 space-y-2.5" data-testid="credit-purchase-history">
      <h3 className="text-sm font-black text-white flex items-center gap-2"><Coins className="w-4 h-4 text-amber-400" /> {L('Historique des achats de crédits', 'Credit purchase history')}</h3>
      {items === null ? (
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
      ) : (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="flex items-center justify-between text-xs" data-testid={`credit-purchase-row-${i}`}>
              <span className="text-zinc-400">{new Date(it.paid_at).toLocaleDateString(en ? 'en-US' : 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              <span className="text-zinc-300 font-semibold">+{it.credits} {L('crédits', 'credits')}</span>
              <span className="text-white font-bold">{it.amount} {(it.currency || 'chf').toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProfilTab = ({ en, toggleLanguage, canInstall, onInstallClick, onLogout }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const tour = useTour();
  const [neverShow, setNeverShowState] = useState(() => tour?.isNeverShow?.() || false);
  const { textScale, setTextScale, highContrast, setHighContrast } = useClientSettings();

  const handleReplay = () => { setNeverShowState(false); tour?.setNeverShow(false); tour?.start(); };
  const handleToggleTours = (checked) => {
    const disabled = !checked;
    setNeverShowState(disabled);
    tour?.setNeverShow(disabled);
  };

  return (
    <div className="space-y-5" data-testid="profil-tab">
      <h2 className="text-xl font-black text-white flex items-center gap-2">
        <UserCog className="w-5 h-5 text-primary" /> {L('Profil & Réglages', 'Profile & Settings')}
      </h2>

      <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-1.5 divide-y divide-white/5" data-testid="profil-settings-card">
        <motion.button whileTap={{ scale: 0.98 }} onClick={toggleLanguage} className={ROW} data-testid="profil-lang-toggle">
          <span className="flex items-center gap-3 text-sm font-semibold text-white"><Globe className="w-4.5 h-4.5 text-primary" /> {L('Langue', 'Language')}</span>
          <span className="inline-flex items-center gap-1 text-xs font-bold text-zinc-400">{en ? 'English' : 'Français'} <ChevronRight className="w-3.5 h-3.5" /></span>
        </motion.button>

        <div className={ROW}>
          <span className="flex items-center gap-3 text-sm font-semibold text-white"><Moon className="w-4.5 h-4.5 text-primary" /> {L('Thème', 'Theme')}</span>
          <ThemeToggle />
        </div>

        {canInstall && (
          <motion.button whileTap={{ scale: 0.98 }} onClick={onInstallClick} className={ROW} data-testid="profil-install-app-btn">
            <span className="flex items-center gap-3 text-sm font-semibold text-white"><Download className="w-4.5 h-4.5 text-primary" /> {L("Installer l'app", 'Install app')}</span>
            <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
          </motion.button>
        )}

        <div className={ROW}>
          <span className="flex items-center gap-3 text-sm font-semibold text-white"><Compass className="w-4.5 h-4.5 text-primary" /> {L('Tours guidés', 'Guided tours')}</span>
          <Switch checked={!neverShow} onCheckedChange={handleToggleTours} data-testid="profil-tours-toggle" />
        </div>

        <motion.button whileTap={{ scale: 0.98 }} onClick={onLogout} className={`${ROW} text-red-400 hover:bg-red-500/10`} data-testid="profil-logout-btn">
          <span className="flex items-center gap-3 text-sm font-semibold"><LogOut className="w-4.5 h-4.5" /> {L('Déconnexion', 'Log out')}</span>
        </motion.button>
      </div>

      <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4 space-y-4" data-testid="profil-accessibility-card">
        <h3 className="text-sm font-black text-white flex items-center gap-2"><Type className="w-4 h-4 text-primary" /> {L('Accessibilité', 'Accessibility')}</h3>

        <div>
          <p className="text-xs font-semibold text-zinc-400 mb-2">{L('Taille du texte', 'Text size')}</p>
          <div className="grid grid-cols-3 gap-2" data-testid="profil-text-scale-control">
            {TEXT_SCALE_LEVELS.map((lvl) => (
              <button key={lvl} onClick={() => setTextScale(lvl)}
                className={`py-2.5 rounded-xl text-xs font-bold border transition-colors ${textScale === lvl ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-white/5 text-zinc-300 border-white/10 hover:border-primary/40'}`}
                data-testid={`profil-text-scale-${lvl}`}>
                {en ? TEXT_SCALE_LABELS[lvl].en : TEXT_SCALE_LABELS[lvl].fr}
              </button>
            ))}
          </div>
        </div>

        <div className={ROW.replace('hover:bg-white/5', '')}>
          <span className="flex items-center gap-3 text-sm font-semibold text-white"><Contrast className="w-4.5 h-4.5 text-primary" /> {L('Contraste élevé', 'High contrast')}</span>
          <Switch checked={highContrast} onCheckedChange={setHighContrast} data-testid="profil-high-contrast-toggle" />
        </div>
      </div>

      <motion.button
        whileTap={{ scale: 0.98 }} onClick={handleReplay}
        className="w-full flex items-center justify-between p-4 bg-white/[0.035] hover:bg-white/[0.06] border border-white/10 hover:border-primary/50 rounded-2xl group transition-all cursor-pointer"
        data-testid="profil-replay-tour-btn"
      >
        <span className="flex items-center gap-3 text-sm font-bold text-white">
          <PlayCircle className="w-5 h-5 text-primary transition-transform group-hover:rotate-90" /> {L('Revoir le tour guidé', 'Replay the guided tour')}
        </span>
        <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-primary transition-colors" />
      </motion.button>

      <div id="abonnement-section">
        <SubscriptionTab en={en} />
      </div>

      <CreditPurchaseHistory en={en} />
    </div>
  );
};

export default ProfilTab;

